import { CompanyId } from "@custom-types/base-types";
import {
  CreditSubscription,
  ExpiringCredits,
} from "@custom-types/credit-types";
import {
  ProjectLaunchTarget,
  SdbCompany,
} from "@custom-types/sdb-company-types";
import { createSelector } from "@reduxjs/toolkit";
import { runtimeConfig } from "@src/runtime-config";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  SdbCompaniesAdapter,
  SdbCompanyState,
} from "@store/sdb-company/sdb-company-slice";
import { RootState } from "@store/store-helper";
import { StringEntityId } from "@store/store-types";
import {
  processSubscriptionConstraints,
  SPHERE_LEGACY_MIGRATED_TAG,
} from "@utils/sdb-company-utils";
import { addNumberedEnv } from "@utils/url-utils";
import { DateTimeUtils } from "@stellar/utils";
import {
  WHITELISTED_IDENTIFIERS,
  DEFAULT_SUBSCRIPTION_NAME_LEGACY_HOLOBUILDER,
  ALL_IGNORED_LABELS_LOWER_CASE,
  LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE,
  LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE,
  DEFAULT_SUBSCRIPTION_NAME_LEGACY_WEB_SHARE,
  VALUE_PRICING_MODEL_LABEL,
} from "@src/constants/subscription-constants";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import {
  getSubscriptionRemainingDays,
  isCompanyIdFromVPMList,
  isTrialSubscription,
} from "@utils/subscription-utils";
import { SubscriptionStatus } from "@custom-types/subscriptions-types";

interface ProjectLaunchTargetSelector {
  /** Defines the default open project target for the selected company. */
  defaultOpenProjectTarget: ProjectLaunchTarget;

  /** Defines whether the default application setting should be hidden */
  shouldShowDefaultAppSetting: boolean;

  /** Defines whether the open in webEditor/HoloBuilder button should be shown or not */
  shouldShowOpenInWebEditorBtn: boolean;
}

export interface CompanyCreditSubscriptions {
  /** The available credit subscriptions */
  availableSubscriptions: CreditSubscription[];

  /**
   *  The expired credit subscriptions
   *  This includes subscriptions that have no available credits or the subscription has expired.
   */
  expiredSubscriptions: CreditSubscription[];
}

/** Maximum end year, used to exclude subscriptions */
const MAX_PLAN_END_YEAR = 2099;

/**
 * Returns the ID of the selected SdbCompany
 */
export const selectedSdbCompanyIdSelector: (state: RootState) => string | null =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return state.sdbCompany.selectedSdbCompanyId;
    }
  );

/**
 * Checks if the given id is the same as the currently selected sdbCompany
 */
export function isSdbCompanySelectedSelector(
  sdbCompanyId: CompanyId
): (state: RootState) => boolean {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return selectedSdbCompanyIdSelector(state) === sdbCompanyId;
    }
  );
}

/**
 * Returns the current selected SdbCompany
 */
export const selectedSdbCompanySelector: (
  state: RootState
) => SdbCompany | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const sdbCompanyId = selectedSdbCompanyIdSelector(state);
    if (!sdbCompanyId) {
      return null;
    }
    // Find the specific sdb-company using the id.
    return (
      SdbCompaniesAdapter.getSelectors().selectById(
        state.sdbCompany,
        sdbCompanyId
      ) ?? null
    );
  }
);

/**
 * Returns whether the selected workspace can access the HB Dashboard.
 */
export const canAccessHBDashboardSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const hasOpenInWebEditorFeature =
        isFeatureAvailableInCompanySelector("open-in-webeditor")(state);
      const isSphereLegacyMigrated =
        selectedSdbCompanySelector(state)?.isSphereLegacyMigrated ?? false;

      // Only workspaces with the "open-in-webeditor" feature enabled that are not migrated sphere legacy
      // workspaces are allowed to access the HB Dashboard.
      return hasOpenInWebEditorFeature && !isSphereLegacyMigrated;
    }
  );

/**
 * Checks if the selected workspace should see the "Open in WebEditor" option.
 * For now it is not shown to any company that was migrated from Sphere Legacy.
 * @param state - The current state.
 * @returns True if any of the required features are enabled, false otherwise.
 */
const shouldShowOpenInWebEditorBtnSelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const hasOpenInWebEditorFeature =
        isFeatureAvailableInCompanySelector("open-in-webeditor")(state);

      // If the company has the open in web editor feature, we don't care about anything else and show it.
      if (hasOpenInWebEditorFeature) {
        return true;
      }

      // Sphere Legacy companies should not see the button.
      // In the future, once all companies are migrated to the new XG plans
      // we should remove this check and only check for the company features.
      if (selectedSdbCompanySelector(state)?.isSphereLegacyMigrated) {
        return false;
      }

      const hasSphereXGAddonPointCloud = isFeatureAvailableInCompanySelector(
        "sphere-xg-addon-point-cloud"
      )(state);
      // If the company has the sphereXGAddonPointCloud feature, we don't show the button.
      // This is not a problem for companies on the Point Cloud + 360 plan because
      // the 360 plan has the open in web editor feature, so it will early return true.
      if (hasSphereXGAddonPointCloud) {
        return false;
      }

      // By default we show the button, this is often the case with companies with the minimal bundle,
      // and expired trials.
      return true;
    }
  );

/**
 * Returns the selected sdbCompany name
 */
export const selectedSdbCompanyNameSelector: (
  state: RootState
) => string | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return selectedSdbCompanySelector(state)?.name ?? null;
  }
);

/**
 * Returns all sdbCompany of the user
 */
export const sdbCompaniesSelector: (state: RootState) => SdbCompany[] =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return (
        SdbCompaniesAdapter.getSelectors()
          .selectAll(state.sdbCompany)
          // Filters out migrated workspaces if the flag is enabled.
          // Do not do this on filteredSdbCompaniesSelector, so that the switch workspaces menu
          // can benefit from this filter.
          .filter((company) => {
            if (
              state.ui.isHideMigratedWorkspacesEnabled &&
              company.tags.includes(SPHERE_LEGACY_MIGRATED_TAG)
            ) {
              return false;
            }
            return true;
          })
          .map((company) => {
            let url = company.url;
            if (
              company.type === "company" &&
              runtimeConfig.isNumberedEnv &&
              runtimeConfig.numberedEnv
            ) {
              url = addNumberedEnv(url, runtimeConfig.numberedEnv);
            }
            return {
              ...company,
              url,
            };
          })
      );
    }
  );

/**
 * Returns all sdbCompany of the user, filtered by the search text
 */
export const filteredSdbCompaniesSelector: (state: RootState) => SdbCompany[] =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const search = state.ui.search.debouncedSearchText.toLowerCase();
      return sdbCompaniesSelector(state).filter((company) =>
        company.name.toLowerCase().includes(search)
      );
    }
  );

/**
 * Returns all Ids of sdbCompany of the user
 */
export const sdbCompanyIdsSelector: (state: RootState) => StringEntityId[] =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return SdbCompaniesAdapter.getSelectors().selectIds(state.sdbCompany);
    }
  );

/**
 * Returns all companies of the user.
 */
export const companiesSelector: (state: RootState) => SdbCompany[] =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return sdbCompaniesSelector(state).filter(
        ({ type }) => type === "company"
      );
    }
  );

/**
 * Returns all admin workspaces of the user.
 */
export const adminWorkspacesSelector: (state: RootState) => SdbCompany[] =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return sdbCompaniesSelector(state).filter(({ type }) => type === "admin");
    }
  );

/**
 * Returns whether the selected company has the given feature.
 *
 * @param featureIdentifier The identifier of the feature to check.
 * @returns True if the feature is enabled, false otherwise.
 */
export function isFeatureAvailableInCompanySelector(
  featureIdentifier: FaroCoreApiTypes.FeatureIdentifier
): (state: RootState) => boolean {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const features = availableFeaturesInCompanySelector(state);
      return (features ?? []).includes(featureIdentifier);
    }
  );
}

/** Returns whether the provided feature is enabled or not */
export function isFeatureEnabledInCompany(
  featureIdentifier: FaroCoreApiTypes.FeatureIdentifier
): (state: RootState) => boolean {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const companyContext = selectedCompanyContextSelector(state);
      if (!companyContext) {
        return false;
      }

      return companyContext.featuresAvailable.some(
        (feature) =>
          feature.identifier === featureIdentifier && feature.enabled === true
      );
    }
  );
}

/**
 * Returns the fetching properties of the sdb-company slice.
 */
export const fetchingSdbCompanyFlagsSelector: (
  state: RootState
) => SdbCompanyState["fetching"] = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return state.sdbCompany.fetching;
  }
);

/**
 * Returns true if any fetching regarding company-slice is true.
 */
export const isFetchingSdbCompanySelector: (state: RootState) => boolean =
  createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      const {
        fetchSelectedCompanyContext,
        isFetchingSdbCompanies,
        isFetchingCompanyBrandingSetting,
        isFetchingCompanyCommunicationSettings,
        isFetchingCompanySubscriptions,
      } = state.sdbCompany.fetching;

      return (
        fetchSelectedCompanyContext === "pending" ||
        isFetchingCompanyBrandingSetting ||
        isFetchingSdbCompanies ||
        isFetchingCompanyCommunicationSettings ||
        isFetchingCompanySubscriptions
      );
    }
  );

/**
 * Returns company context for the selected company.
 */
export const selectedCompanyContextSelector: (
  state: RootState
) => FaroCoreApiTypes.GetCompanyContext | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return state.sdbCompany.selectedCompanyContext;
  }
);

/** Returns all company features for the selected company */
export const availableFeaturesInCompanySelector: (
  state: RootState
) => FaroCoreApiTypes.FeatureIdentifier[] | null = createSelector(
  selectedCompanyContextSelector,
  (companyContext) => {
    if (!companyContext) {
      return null;
    }
    return companyContext.featuresAvailable.map(
      (feature) => feature.identifier
    );
  }
);

/**
 * Returns branding settings for the selected company.
 */
export const companyBrandingSettingsSelector: (
  state: RootState
) => FaroCoreApiTypes.BrandingSettings | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return state.sdbCompany.brandingSettings;
  }
);

/** Get company details by providing the companyId */
export function getCompanyByIdSelector(
  companyId: CompanyId
): (state: RootState) => SdbCompany | null {
  return createSelector(
    (state: RootState) => state,
    (state: RootState) => {
      return (
        SdbCompaniesAdapter.getSelectors().selectById(
          state.sdbCompany,
          companyId
        ) ?? null
      );
    }
  );
}

/**
 * Returns communication settings for the selected company.
 */
export const companyCommunicationSettingsSelector: (
  state: RootState
) => FaroCoreApiTypes.CompanyCommunicationSettings | null = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    return state.sdbCompany.communicationSettings;
  }
);

/**
 * Returns the default open project target for the selected company, based on the company settings.
 * If the company settings are set, it will favour the company settings.
 * If the company settings are not set, it will favour the provided default target.
 * @param defaultTarget  default target if the company settings are not set.
 * @param isSphereViewerDefaultSetting  company setting for the default target. Null if not set.
 * @returns The application to open the projects in by default.
 */
export function getDefaultOpenProjectTarget(payload: {
  defaultTarget: ProjectLaunchTarget;
  isSphereViewerDefaultSetting: boolean | undefined | null;
}): ProjectLaunchTarget {
  // Returns defaultTarget if the user has not set any target application.
  if (
    payload.isSphereViewerDefaultSetting === undefined ||
    payload.isSphereViewerDefaultSetting === null
  ) {
    return payload.defaultTarget;
  }
  return payload.isSphereViewerDefaultSetting
    ? ProjectLaunchTarget.sphereViewer
    : ProjectLaunchTarget.webEditor;
}

/** Returns the default open project application for the selected company. */
export const defaultProjectLaunchTargetSelector: (
  state: RootState
) => ProjectLaunchTargetSelector = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    const shouldShowOpenInWebEditorBtn =
      shouldShowOpenInWebEditorBtnSelector(state);

    const isSphereViewerDefaultSetting =
      companyCommunicationSettingsSelector(state)?.sphereViewerIsDefault;

    const hasSphereXGAddonPointCloud = isFeatureEnabledInCompany(
      "sphere-xg-addon-point-cloud"
    )(state);
    const hasGlobalPointCloudWrite = isFeatureEnabledInCompany(
      "global-pointcloud-write"
    )(state);
    const hasGlobalOrbisProcessing = isFeatureEnabledInCompany(
      "global-orbis-processing"
    )(state);

    // If the company does not show the open in HoloBuilder button,
    // The default open project target should be Sphere Viewer.
    // It should not let users to change the default application setting.
    if (!shouldShowOpenInWebEditorBtn) {
      return {
        defaultOpenProjectTarget: getDefaultOpenProjectTarget({
          defaultTarget: ProjectLaunchTarget.sphereViewer,
          isSphereViewerDefaultSetting,
        }),
        shouldShowDefaultAppSetting: false,
        shouldShowOpenInWebEditorBtn,
      };
    }

    // If the company has the open in HoloBuilder button enabled,
    // But can view point clouds, the default application should be Sphere Viewer.
    // But it should let the user to change this setting.
    if (
      hasSphereXGAddonPointCloud ||
      hasGlobalPointCloudWrite ||
      hasGlobalOrbisProcessing
    ) {
      return {
        defaultOpenProjectTarget: getDefaultOpenProjectTarget({
          defaultTarget: ProjectLaunchTarget.sphereViewer,
          isSphereViewerDefaultSetting,
        }),
        shouldShowDefaultAppSetting: true,
        shouldShowOpenInWebEditorBtn,
      };
    }

    // If the company has the open in HoloBuilder button enabled,
    // And can not view point clouds, the default application should be Web Editor.
    // But it should let the user to change this setting.
    // Once we have feature parity and we want to promote Sphere Viewer instead of Web Editor,
    // The default value should be changed here.
    return {
      defaultOpenProjectTarget: getDefaultOpenProjectTarget({
        defaultTarget: ProjectLaunchTarget.webEditor,
        isSphereViewerDefaultSetting,
      }),
      shouldShowDefaultAppSetting: true,
      shouldShowOpenInWebEditorBtn,
    };
  }
);

/**
 * Returns the subscriptions of the selected company
 */
export const selectedCompanySubscriptionsSelector = createSelector(
  (state: RootState) => state,
  (state: RootState): FaroCoreApiTypes.CompanySubscription[] => {
    return state.sdbCompany.selectedCompanySubscriptions;
  }
);

/**
 * Returns all credit subscriptions of the selected company
 */
export const allCreditSubscriptionsSelector = createSelector(
  selectedCompanySubscriptionsSelector,
  (
    companySubscriptions: FaroCoreApiTypes.CompanySubscription[]
  ): CreditSubscription[] => {
    if (!companySubscriptions.length) {
      return [];
    }

    const result = companySubscriptions.flatMap((subscription) =>
      processSubscriptionConstraints(subscription)
    );

    const descSortedResult = result.sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
    return descSortedResult;
  }
);

/**
 * Returns all available credit subscriptions of the selected company
 */
export const availableCreditSubsSelector = createSelector(
  allCreditSubscriptionsSelector,
  (allCreditSubscriptions: CreditSubscription[]): CreditSubscription[] => {
    if (!allCreditSubscriptions) {
      return [];
    }

    const now = new Date();

    return allCreditSubscriptions.filter(
      (creditSub) =>
        creditSub.availableCredit > 0 && new Date(creditSub.expiresAt) > now
    );
  }
);

/**
 * Returns the earliest expiring credits of the selected company
 */
export const getEarliestExpiringCreditsSelector = createSelector(
  availableCreditSubsSelector,
  (
    availableSubscriptions: CreditSubscription[]
  ): ExpiringCredits | undefined => {
    if (availableSubscriptions.length === 0) {
      return undefined;
    }

    // Group subscriptions by expiration date and sum the available credits for each date
    const groupedByDate = new Map<string, ExpiringCredits>();

    for (const subscription of availableSubscriptions) {
      // Example: "2025-04-02T15:30:00Z" => "2025-04-02"
      const expirationDate = subscription.expiresAt.split("T")[0];
      const existingSubscription = groupedByDate.get(expirationDate);

      if (!existingSubscription) {
        groupedByDate.set(expirationDate, {
          totalCredits: subscription.availableCredit,
          expiresAt: subscription.expiresAt,
        });
      } else {
        existingSubscription.totalCredits += subscription.availableCredit;
      }
    }

    // Find the earliest expiration date
    let earliest: ExpiringCredits | undefined;
    for (const expiringSubscription of groupedByDate.values()) {
      if (!earliest || expiringSubscription.expiresAt < earliest.expiresAt) {
        earliest = expiringSubscription;
      }
    }
    return earliest;
  }
);

/**
 * Returns the total credit balance of the company
 * This is the sum of all available credits of the subscriptions.
 */
export const companyCreditBalanceSelector = createSelector(
  availableCreditSubsSelector,
  (availableSubscriptions: CreditSubscription[]): number =>
    availableSubscriptions.reduce(
      (total, { availableCredit }) => total + availableCredit,
      0
    )
);

/**
 * Returns the company subscription with the highest priority and with longer expiration date.
 */
export const topPriorityXgSubscriptionSelector = createSelector(
  selectedCompanySubscriptionsSelector,
  (
    companySubscriptions: FaroCoreApiTypes.CompanySubscription[]
  ): FaroCoreApiTypes.XGCompanySubscription | undefined => {
    // Filter subscriptions to only allow active Sphere XG subscriptions and
    // then sort them by priority and expiration date.
    const sortedSubscriptions = companySubscriptions
      .filter((subscription) => subscription.status === "ACTIVE")
      // This filter is also a type guard to FaroCoreApiTypes.XGCompanySubscription
      .filter((subscription) =>
        FaroCoreApiUtils.Feature.isXGSubscription(subscription)
      )
      .sort(FaroCoreApiUtils.Feature.compareXGSubscriptionsByPriority);

    // Return the highest priority subscription
    return sortedSubscriptions?.[0];
  }
);

/** All company subscriptions */
type AllCompanySubscription =
  | FaroCoreApiTypes.XGCompanySubscription
  | FaroCoreApiTypes.CompanySubscription
  | CompanySubscriptionLabelEndDate;

/**
 * Represents the minimal information of a company subscription.
 * Only includes the `label` and `endDate` fields, which are necessary
 * to identify and compare subscriptions without sending the full object.
 */
type CompanySubscriptionLabelEndDate = Partial<
  Pick<FaroCoreApiTypes.CompanySubscription, "label" | "endDate">
>;

/** Create an empty custom subscription */
export function createCustomSubscription(
  subscription?: CompanySubscriptionLabelEndDate
): CompanySubscriptionLabelEndDate {
  const originalLabelLowerCase = subscription?.label?.toLowerCase() ?? "";
  let displayLabel: string | undefined;

  if (
    LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE.has(
      originalLabelLowerCase
    )
  ) {
    displayLabel = DEFAULT_SUBSCRIPTION_NAME_LEGACY_HOLOBUILDER;
  } else if (
    LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE.has(originalLabelLowerCase)
  ) {
    displayLabel = DEFAULT_SUBSCRIPTION_NAME_LEGACY_WEB_SHARE;
  }

  return {
    label: displayLabel,
    endDate: subscription?.endDate,
  };
}

/**
 * If there is no XG subscription assigned in the company, it tries to find a legacy subscription,
 * based on some factors like known legacy labels, or the furthest end date.
 */
export const customSubscriptionSelector = createSelector(
  selectedCompanySubscriptionsSelector,
  selectedSdbCompanyIdSelector,
  (
    subscriptions,
    selectedSdbCompanyId
  ): CompanySubscriptionLabelEndDate | FaroCoreApiTypes.CompanySubscription => {
    // Step 1: Filter out non-active subscriptions, ignored labels and subscriptions with MAX_PLAN_END_YEAR
    const filteredSubscriptions = subscriptions.filter((sub) => {
      if (sub.status !== "ACTIVE") {
        return false;
      }

      if (
        sub.label &&
        ALL_IGNORED_LABELS_LOWER_CASE.has(sub.label.toLowerCase())
      ) {
        return false;
      }

      if (
        sub.endDate &&
        new Date(sub.endDate).getFullYear() === MAX_PLAN_END_YEAR
      ) {
        return false;
      }

      return true;
    });

    // Step 2: Sort subscriptions by end date ascending. The ones expiring soonest first.
    const sortedByEndDateAsc = filteredSubscriptions.sort((a, b) =>
      DateTimeUtils.diffBetweenTwoDatesInDays(a.endDate, b.endDate)
    );

    // Create a set of all legacy subscription labels in lowercase for easy lookup
    const allLegacyLabels =
      LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE.union(
        LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE
      );

    // Step 3: Check for known legacy subscriptions both Holobuilder and Sphere
    const legacySubscription = sortedByEndDateAsc.find(
      (sub) => sub.label && allLegacyLabels.has(sub.label.toLowerCase())
    );

    if (legacySubscription) {
      return createCustomSubscription(legacySubscription);
    }

    // Step 4: Check for subscription with "user_role_fm" feature
    // There is a special feature called user_role_fm that only paid subscriptions have.
    // This is not ideal because we should rely better on labels to whitelist known legacy subscriptions,
    // but to this point the subscriptions do not provide any other way to identify paid legacy subscriptions.
    const subscriptionWithUserRoleFm = sortedByEndDateAsc.find((subscription) =>
      subscription.features?.some(
        (feature) => feature.identifier === "user_role_fm"
      )
    );
    if (subscriptionWithUserRoleFm) {
      return createCustomSubscription({
        // We consider such subscriptions as having the Value Pricing Model
        label: VALUE_PRICING_MODEL_LABEL,
        endDate: subscriptionWithUserRoleFm.endDate,
      });
    }

    // Step 5: If you reach this point, it could be that companies have a VPM subscription assigned to a project,
    // The only way would be to fetch every single project and check their features, but could be thousands of projects.
    // TODO: We might include a specific feature to such companies to identify them better in the future.
    // For now we use the company id and a workaround to identify by company id.
    if (isCompanyIdFromVPMList({ companyId: selectedSdbCompanyId })) {
      return createCustomSubscription({
        label: VALUE_PRICING_MODEL_LABEL,
        // Since we identified them by company id, we don't have an end date.
      });
    }

    // Step 6: Return a default one, assuming there is no active subscription
    return createCustomSubscription();
  }
);

/**
 * Returns the active subscription for a company, prioritizing top-priority XG subscriptions.
 * If none exists, returns a custom subscription (Plan FM, furthest end date, or a default placeholder).
 */
export const activeSubscriptionSelector = createSelector(
  topPriorityXgSubscriptionSelector,
  customSubscriptionSelector,
  (topPrioritySubscription, customSubscription): AllCompanySubscription => {
    if (topPrioritySubscription) {
      return topPrioritySubscription;
    }

    return customSubscription;
  }
);

/**
 * Selector to get the company's quotas filtered by a whitelist.
 *
 * Only quotas whose `identifier` is present in `WHITELISTED_IDENTIFIERS`
 * will be returned. This ensures that only relevant quotas are exposed
 * for UI components or business logic.
 */
export const selectQuotasSelector = createSelector(
  selectedCompanyContextSelector,
  (context): FaroCoreApiTypes.ConstraintQuota[] => {
    if (!context) {
      return [];
    }

    return context.quotas.filter((quota) =>
      WHITELISTED_IDENTIFIERS.includes(quota.identifier)
    );
  }
);

interface StorageUsage {
  /** The storage that was already used */
  used: number;

  /** The number of max storage that company can use */
  max?: number;
}

/**
 * Selector to get the storage usage for the company.
 *
 * This combines data from two sources:
 * 1. The raw `sizeInBytes` from `context.storage.projects.total`.
 * 2. The storage quota from `context.quotas` (if available).
 */
export const selectStorageUsedSelector = createSelector(
  selectedCompanyContextSelector,
  (context): StorageUsage | null => {
    if (!context) {
      return null;
    }
    const storageFromQuotas = context.quotas.find(
      (quotas) => quotas.identifier === "storage"
    );

    if (!storageFromQuotas) {
      return null;
    }

    return {
      used: storageFromQuotas.current ?? 0,
      max: storageFromQuotas.limit ?? undefined,
    };
  }
);

/** Returns the company subscription status */
export const subscriptionStatusSelector = createSelector(
  (state: RootState) => state,
  topPriorityXgSubscriptionSelector,
  (state, subscription): SubscriptionStatus => {
    const isTrial = isTrialSubscription(subscription);
    const subscriptionRemainingDays =
      getSubscriptionRemainingDays(subscription);
    const isCoreProductFeatureEnabled =
      isFeatureAvailableInCompanySelector("core-product")(state);

    if (subscriptionRemainingDays === null || subscriptionRemainingDays < 0) {
      return isTrial ? "trial-ended" : "subscription-ended";
    }

    if (isTrial) {
      return "trial-active";
    }

    return isCoreProductFeatureEnabled
      ? "subscription-active"
      : "subscription-ended";
  }
);
